import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React, { ReactNode, useState } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  EntityErrorFilter,
  EntityOrphanFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { useLocation, useNavigate } from 'react-router-dom';

/** @public */
export type CatalogReactEntityProcessingStatusPickerClassKey = 'input';

let isLoaded: boolean = false;

const useStyles = makeStyles(
  {
    root: {},
    input: {},
    label: {},
  },
  {
    name: 'CatalogReactEntityProcessingStatusPickerPicker',
  },
);

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/** @public */
export const EntityProcessingStatusPicker = () => {
  const classes = useStyles();
  const { updateFilters } = useEntityList();

  const availableAdvancedItems = ['Is Orphan', 'Has Error'];
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const processingStatus: string[] = (queryParams.get('processingStatus') || '')
    .split(',')
    .filter(value => availableAdvancedItems.includes(value));

  const [selectedAdvancedItems, setSelectedAdvancedItems] =
    useState<string[]>(processingStatus);

  if (!isLoaded) {
    orphanChange(selectedAdvancedItems.includes('Is Orphan'));
    errorChange(selectedAdvancedItems.includes('Has Error'));
    isLoaded = true;
  }

  function orphanChange(value: boolean) {
    updateFilters({
      orphan: value ? new EntityOrphanFilter(value) : undefined,
    });
  }

  function errorChange(value: boolean) {
    updateFilters({
      error: value ? new EntityErrorFilter(value) : undefined,
    });
  }

  return (
    <Box className={classes.root} pb={1} pt={1}>
      <Typography className={classes.label} variant="button" component="label">
        Processing Status
        <Autocomplete<string, true>
          PopperComponent={popperProps => (
            <div {...popperProps}>{popperProps.children as ReactNode}</div>
          )}
          multiple
          disableCloseOnSelect
          options={availableAdvancedItems}
          value={selectedAdvancedItems}
          onChange={(_: object, value: string[]) => {
            setSelectedAdvancedItems(value);
            orphanChange(value.includes('Is Orphan'));
            errorChange(value.includes('Has Error'));
            queryParams.set('processingStatus', value.join(','));
            navigate(`${location.pathname}?${queryParams.toString()}`, {
              replace: true,
            });
          }}
          renderOption={(option, { selected }) => (
            <FormControlLabel
              control={
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  checked={selected}
                />
              }
              onClick={event => event.preventDefault()}
              label={option}
            />
          )}
          size="small"
          popupIcon={
            <ExpandMoreIcon data-testid="processing-status-picker-expand" />
          }
          renderInput={params => (
            <TextField
              {...params}
              className={classes.input}
              variant="outlined"
            />
          )}
        />
      </Typography>
    </Box>
  );
};
